import { useUserData } from '../hooks/useUserData';
import { ProxyApi } from '../api/rndApi';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpsellSuccessModal } from '../components/UpsellSuccessModal';
import { SHOP_UPSELLS } from '../constants/shop';
import WoodBackgroundImage from '../assets/images/wood-background.png';
import CustomCover from '../components/CustomCover';

const keyframes = `
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.6;
    }
  }
`;

const Skeleton = ({ width, height, style, className }) => {
  const skeletonStyle = {
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
    width,
    height,
    ...style,
  };
  return (
    <>
      <style>{keyframes}</style>
      <div style={skeletonStyle} className={className} />
    </>
  );
};

const ShopItemSkeleton = () => (
  <div
    className="align-items-center bg-white p-4 rounded-4"
    style={{ flex: '1', display: 'flex', flexDirection: 'column' }}
  >
    <Skeleton
      height="345px"
      width="236px"
      style={{ width: '236px', borderRadius: '8px', marginBottom: '16px' }}
    />
    <Skeleton width="70%" height="24px" style={{ marginBottom: '12px' }} />
    <Skeleton width="40%" height="32px" style={{ marginBottom: '8px' }} />
    <Skeleton width="55%" height="21px" style={{ marginBottom: '16px' }} />
    <Skeleton width="120px" height="45px" style={{ borderRadius: '8px' }} />
  </div>
);

function ShopPage() {
  const { t } = useTranslation();
  const { userData } = useUserData();

  const [upsellsLoading, setUpsellsLoading] = useState(true);
  const [upsells, setUpsells] = useState(null);
  const [upsellUrls, setUpsellUrls] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUpsellKey, setSelectedUpsellKey] = useState(null);
  const [selectedUpsellImage, setSelectedUpsellImage] = useState(null);

  useEffect(() => {
    if (!userData || !userData?.code || !upsellsLoading) return;

    (async () => {
      try {
        const response = await ProxyApi.fetchUpsells(userData?.code);
        const data = response?.data.filter(
          (u) =>
            (u.key.includes('product-digital-book-credits-v1') &&
              u.key !== 'product-digital-book-credits-v1-plus2x') ||
            u.key === 'upsell-memory-recall'
        );
        data.sort((a, b) => a.prices[0].final_price - b.prices[0].final_price);
        setUpsells(data);
        setUpsellUrls(response?.upsell_urls);
      } catch (error) {
        console.error(error);
      } finally {
        setUpsellsLoading(false);
      }
    })();
  }, [userData, upsellsLoading]);

  const handleClick = (upsellKey, upsellImage) => {
    setSelectedUpsellKey(upsellKey);
    setShowModal(true);
    setSelectedUpsellImage(upsellImage);
  };

  return (
    <>
      <div style={{ marginTop: '40px' }}>
        <p style={{ marginBottom: '24px', fontSize: '24px', fontWeight: 600 }}>
          {t('common.shop')}
        </p>
        <div className="login-screen-container">
          {upsellsLoading
            ? Object.keys(SHOP_UPSELLS).map((key) => (
                <ShopItemSkeleton key={key} />
              ))
            : upsells.map((upsell) => {
                const hardcodedData = SHOP_UPSELLS[upsell.key];

                const quantity =
                  upsell?.properties?.find((p) => p.name === 'quantity')
                    ?.value || hardcodedData?.quantity;
                const image =
                  upsell?.properties?.find((p) => p.name === 'image')?.value ||
                  hardcodedData?.image;

                const initialPrice = upsell?.prices[0]?.price;
                const finalPrice = upsell?.prices[0]?.final_price;
                const discountPercents = (
                  100 -
                  (finalPrice / initialPrice) * 100
                ).toFixed(0);

                return (
                  <div
                    key={upsell.id}
                    className="align-items-center bg-white p-4 rounded-4"
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      style={{
                        flex: '0 0 45%',
                        backgroundImage: `url(${WoodBackgroundImage})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        borderRadius: '8px',
                        minHeight: '345px',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                      }}
                    >
                      {userData?.cover &&
                      upsell.key !== 'upsell-memory-recall' ? (
                        <div style={{ position: 'relative' }}>
                          {Array.from({ length: quantity || 2 }).map(
                            (_, index) => {
                              const totalBooks = quantity || 2;
                              const horizontalSpacing = 6;
                              const verticalSpacing = 8;
                              const horizontalCenterOffset =
                                ((totalBooks - 1) * horizontalSpacing) / 2;
                              const verticalCenterOffset =
                                ((totalBooks - 1) * verticalSpacing) / 2;

                              return (
                                <div
                                  key={index}
                                  style={{
                                    position:
                                      index === 0 ? 'relative' : 'absolute',
                                    top:
                                      index === 0
                                        ? verticalCenterOffset
                                        : -verticalSpacing * index +
                                          verticalCenterOffset,
                                    left:
                                      index === 0
                                        ? -horizontalCenterOffset
                                        : horizontalSpacing * index -
                                          horizontalCenterOffset,
                                  }}
                                >
                                  <CustomCover
                                    cover={userData?.cover}
                                    width={236}
                                    coverPhoto={userData?.cover_photo}
                                    withPhoto={userData?.cover < 7}
                                    name={
                                      index === totalBooks - 1
                                        ? userData?.name
                                        : undefined
                                    }
                                  />
                                </div>
                              );
                            }
                          )}
                        </div>
                      ) : (
                        <img
                          src={image}
                          alt="book"
                          style={{
                            width: '236px',
                            height: '236px',
                            objectFit: 'contain',
                          }}
                        />
                      )}
                    </div>
                    <p
                      style={{
                        marginBottom: 0,
                        marginTop: '16px',
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'center',
                      }}
                    >
                      {quantity && `${quantity}x `}
                      {t(hardcodedData?.titleKey)}
                    </p>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                      }}
                    >
                      <p
                        style={{
                          marginBottom: 0,
                          fontSize: '32px',
                          fontWeight: 600,
                        }}
                      >
                        ${finalPrice}
                      </p>
                      <p
                        style={{
                          marginBottom: 0,
                          fontSize: '12px',
                          color: '#FFF',
                          backgroundColor: '#C62828',
                          borderRadius: '2px',
                          fontWeight: 600,
                          padding: '2px 4px',
                        }}
                      >
                        {t('profileScreen.save')} {discountPercents}%
                      </p>
                    </div>
                    <p
                      style={{
                        marginBottom: 0,
                        fontSize: '14px',
                        textAlign: 'center',
                      }}
                    >
                      {t('shopScreen.regularPrice')}:{' '}
                      <span style={{ fontWeight: 600, color: '#C62828' }}>
                        ${initialPrice}
                      </span>
                    </p>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <div
                        onClick={() => handleClick(upsell.key, image)}
                        className="button-black"
                        style={{
                          fontWeight: 500,
                          display: 'inline-block',
                          cursor: 'pointer',
                          opacity: 1,
                        }}
                      >
                        {t('shopScreen.orderNow')}
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>
      </div>
      {selectedUpsellKey && (
        <UpsellSuccessModal
          onHide={() => setShowModal(false)}
          upsellPurchaseUrl={upsellUrls[selectedUpsellKey]}
          upsellImage={selectedUpsellImage}
          visible={showModal}
        />
      )}
    </>
  );
}

export default ShopPage;
